// src/pages/Servicerequests.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styles from "../Styles/Servicerequests.module.css";

import { useSelectedCompany } from "../Commoncontext/SelectedCompanyContext";
import { useUsers } from "../Commoncontext/UsersContext";
import { useToast } from "../Commoncontext/ToastContext";
import { useServicerequests } from "../Commoncontext/ServicerequestsContext";

import IconServicerequests from "../Images/IconServicerequests";
import { fmtDateTime } from "../Utils/dates";

import MessageThread from "../Components/MessageThread";
import AttachmentsPanel from "../Components/AttachmentPanel";
import SimpleTable from "../Components/SimpleTable";
import Modal from "../Components/Modal"; // ✅ LISÄYS

import {
  isPrivileged,
  computePermissions,
  filterVisibleMessages,
} from "../Utils/servicerequestsPermissions";

import useEditableEntity from "../Components/useEditableEntity";

/* =============================================================================
   LABELIT + JÄRJESTYKSET
============================================================================= */
const TYPE_LABEL = {
  huolto: "Huoltopyyntö",
  muutostyo: "Muutostyöilmoitus",
  avain: "Avaintilaus",
  asiakirja: "Asiakirjatilaus",
  viesti: "Vapaa viesti",
};

const STATUS_ORDER = ["uusi", "vastaanotettu", "tyon_alla", "valmis", "suljettu"];
const STATUS_LABEL = {
  uusi: "Uusi",
  vastaanotettu: "Vastaanotettu",
  tyon_alla: "Työn alla",
  valmis: "Valmis",
  suljettu: "Suljettu",
};

const PRIORITY_ORDER = ["low", "normal", "high", "urgent"];
const PRIORITY_LABEL = {
  low: "Matala",
  normal: "Normaali",
  high: "Korkea",
  urgent: "Kiireellinen",
};

/* =============================================================================
   APURIT
============================================================================= */

/** Luo tekijän nimen yhdestä paikasta, jotta sitä ei tarvitse toistaa. */
function getUserDisplayName(user) {
  return user ? `${user?.etunimi || ""} ${user?.sukunimi || ""}`.trim() : "";
}

/** Normalisoi request -> draft (vain muokattavat kentät). */
function normalizeRequestToDraft(sr) {
  return {
    type: sr?.type || "huolto",
    status: sr?.status || "uusi",
    priority: sr?.priority || "normal",
    apartmentId: sr?.apartmentId || "",
    description: typeof sr?.description === "string" ? sr.description : "",
  };
}

/** Laskee onko draft muuttunut verrattuna baseen (dirty). */
function computeDirtyDraft(d, b) {
  if (!b) return true;
  return (
    (d?.type || "huolto") !== (b?.type || "huolto") ||
    (d?.status || "uusi") !== (b?.status || "uusi") ||
    (d?.priority || "normal") !== (b?.priority || "normal") ||
    (d?.apartmentId || "") !== (b?.apartmentId || "") ||
    (d?.description || "") !== (b?.description || "")
  );
}

/* =============================================================================
   PÄÄKOMPONENTTI
============================================================================= */
export default function Servicerequests() {
  const { selectedCompanyId } = useSelectedCompany();
  const { user } = useUsers();
  const { showToast } = useToast();

  const {
    requests,
    loading,
    fetchRequests,
    fetchRequestById,
    updateRequest,
    addMessage,
    deleteMessage,
    createRequest,
    uploadAttachments,
    deleteAttachment,
    deleteRequest, // ✅ LISÄYS (contextiin pitää tulla tämä)
    active,
    setActive,
  } = useServicerequests();

  /* =============================================================================
     UI STATE
  ============================================================================= */
  const [view, setView] = useState("list"); // "list" | "detail"

  // List UI
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileVisibleCount, setMobileVisibleCount] = useState(7);

  // Create modal UI
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createRequestType, setCreateRequestType] = useState("huolto");
  const [createDraft, setCreateDraft] = useState({
    title: "",
    apartmentId: "",
    description: "",
    priority: "normal",
  });
  const createTitleRef = useRef(null);

  // Detail chat UI
  const [messageText, setMessageText] = useState("");
  const [messageVisibility, setMessageVisibility] = useState("public");
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // One shared busy flag for all “actions” on this page (save/status/attachments/create)
  const [busy, setBusy] = useState(false);
  const isBusy = busy || isSendingMessage;

  // ✅ Version conflict UI
  const [conflict, setConflict] = useState({
    open: false,
    server: null,
    action: "",
  });

  const closeConflict = useCallback(() => {
    setConflict({ open: false, server: null, action: "" });
  }, []);

  const openConflict = useCallback((e, action = "") => {
    setConflict({
      open: true,
      server: e?.server || null,
      action,
    });
  }, []);

  // ✅ Delete modal UI (kuten Announcements.js)
  const [selectedRequestForDelete, setSelectedRequestForDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  /* =============================================================================
     EDITABLE DRAFT (useEditableEntity)
     - pidetään tämä ennallaan (toimiva), mutta tehdään käyttö selkeämmäksi.
  ============================================================================= */
  const initialDraft = useMemo(
    () => ({
      type: "huolto",
      status: "uusi",
      priority: "normal",
      apartmentId: "",
      description: "",
    }),
    []
  );

  const {
    draft,
    setField,
    setMany,
    dirty,
    resetFromEntity,
    syncFromEntityIfSafe,
    commitBase,
  } = useEditableEntity({
    initialDraft,
    normalizeEntityToDraft: normalizeRequestToDraft,
    computeDirty: computeDirtyDraft,
    buildPatch: (d) => ({
      type: d.type,
      status: d.status,
      priority: d.priority || "normal",
      apartmentId: d.apartmentId || "",
      description: d.description || "",
    }),
    onSave: async (patch) => ({ savedDraft: patch }), // ei käytetä suoraan tässä tiedostossa
  });

  const activeRequest = active;

  const reloadLatestAndDiscardLocal = useCallback(async () => {
    if (!selectedCompanyId || !activeRequest?.id) return;

    try {
      const fresh = await fetchRequestById(selectedCompanyId, activeRequest.id);
      if (!fresh) return;

      // ✅ yliaja draft + base väkisin uusimmalla
      resetFromEntity(fresh, { force: true });
      // varmistetaan että UI pysyy detailissä
      setActive(fresh);
      showToast("Ladattiin uusimmat tiedot", "success", 1600);
    } catch (err) {
      console.error(err);
      showToast("Uusimpien tietojen lataus epäonnistui", "error", 2500);
    } finally {
      closeConflict();
    }
  }, [
    selectedCompanyId,
    activeRequest?.id,
    fetchRequestById,
    resetFromEntity,
    setActive,
    showToast,
    closeConflict,
  ]);

  /**
   * Laskee käyttäjän oikeudet aktiiviseen pyyntöön
   * → käytetään nappien enable/disable-logiikassa
   */
  const perms = useMemo(
    () =>
      computePermissions({
        user,
        companyId: selectedCompanyId,
        request: activeRequest,
      }),
    [user, selectedCompanyId, activeRequest]
  );

  /**
   * Suodattaa listan niin, että tavallinen käyttäjä näkee vain omansa
   */
  const visibleRequests = useMemo(() => {
    const arr = Array.isArray(requests) ? requests : [];
    if (!selectedCompanyId) return arr;

    const privileged = isPrivileged(user, selectedCompanyId);
    if (privileged) return arr;

    const myUid = user?.uid || null;
    if (!myUid) return [];

    return arr.filter((r) => r?.createdBy?.uid === myUid);
  }, [requests, user, selectedCompanyId]);

  const filteredMobileRequests = useMemo(() => {
    const q = (searchQuery || "").toLowerCase().trim();

    return [...visibleRequests]
      .filter((r) => {
        if (statusFilter !== "all" && r.status !== statusFilter) return false;
        if (typeFilter !== "all" && r.type !== typeFilter) return false;

        if (!q) return true;

        const haystack = `${r.number ?? ""} ${r.title ?? ""} ${
          r.apartmentId ?? ""
        } ${TYPE_LABEL[r.type] ?? ""} ${STATUS_LABEL[r.status] ?? ""}`.toLowerCase();

        return haystack.includes(q);
      })
      .sort((a, b) => {
        const av = a.updatedAt?._seconds ?? a.updatedAt ?? 0;
        const bv = b.updatedAt?._seconds ?? b.updatedAt ?? 0;
        return bv - av;
      });
  }, [visibleRequests, statusFilter, typeFilter, searchQuery]);

  useEffect(() => {
    setMobileVisibleCount(7);
  }, [statusFilter, typeFilter, searchQuery]);

  const mobileRequests = useMemo(() => {
    return filteredMobileRequests.slice(0, mobileVisibleCount);
  }, [filteredMobileRequests, mobileVisibleCount]);

  // ✅ Oikeus poistaa palvelupyyntö listalta (vain privileged)
  const canDeleteRequest = useMemo(
    () => isPrivileged(user, selectedCompanyId),
    [user, selectedCompanyId]
  );

  // ✅ Delete flow (kuten Announcements.js)
  const askDeleteRequest = useCallback((r) => {
    setSelectedRequestForDelete(r);
    setShowDeleteModal(true);
  }, []);

  const confirmDeleteRequest = useCallback(async () => {
    if (!selectedCompanyId || !selectedRequestForDelete?.id) return;
    try {
      await deleteRequest(selectedCompanyId, selectedRequestForDelete.id);
      showToast(
        `Palvelupyyntö #${selectedRequestForDelete.number} poistettu`,
        "success",
        1800
      );
      // jos poistettiin aktiivinen, palaa listaan
      if (activeRequest?.id === selectedRequestForDelete.id) {
        setActive(null);
        setMessageText("");
        setView("list");
        resetFromEntity(null, { force: true });
      }
    } catch (e) {
      console.error(e);
      const code = e?.code || e?.message || "";
      if (String(code).includes("forbidden")) {
        showToast("Ei oikeuksia poistaa palvelupyyntöä", "error", 2200);
      } else {
        showToast("Poisto epäonnistui", "error", 2500);
      }
    } finally {
      setShowDeleteModal(false);
      setSelectedRequestForDelete(null);
    }
  }, [
    selectedCompanyId,
    selectedRequestForDelete,
    deleteRequest,
    showToast,
    activeRequest?.id,
    setActive,
    resetFromEntity,
  ]);

  const columns = useMemo(
    () => [
      {
        key: "number",
        header: "#",
        width: 80,
        accessor: (r) => `#${r.number}`,
        sortAccessor: (r) => Number(r.number ?? 0),
      },
      {
        key: "title",
        header: "Otsikko",
        render: (r) => <span className={styles.clamp}>{r.title}</span>,
        sortAccessor: (r) => (r.title ?? "").toLowerCase(),
      },
      {
        key: "updatedAt",
        header: "Päivitetty",
        accessor: (r) => fmtDateTime(r.updatedAt),
        sortAccessor: (r) => r.updatedAt?._seconds ?? r.updatedAt ?? 0,
        align: "left",
        width: 160,
      },
      {
        key: "type",
        header: "Tyyppi",
        accessor: (r) => TYPE_LABEL[r.type] || r.type,
        sortAccessor: (r) => TYPE_LABEL[r.type] || r.type || "",
      },
      {
        key: "status",
        header: "Tila",
        render: (r) => (
          <span className={`${styles.statusBadge} ${styles[`st_${r.status}`]}`}>
            {STATUS_LABEL[r.status] || r.status}
          </span>
        ),
        sortAccessor: (r) => STATUS_ORDER.indexOf(r.status ?? ""),
      },
    ],
    []
  );

  /**
   * Suodattaa keskustelun viestit käyttäjän oikeuksien mukaan
   */
  const visibleMessages = useMemo(() => {
    const sorted = filterVisibleMessages(activeRequest?.messages || [], {
      privileged: perms.privileged,
    })
      .map((m) => (m?.isDeleted ? { ...m, body: "Viesti poistettu" } : m)) // ✅ LISÄYS
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    return sorted;
  }, [activeRequest?.messages, perms.privileged]);

  /** Hae lista kun company vaihtuu. */
  useEffect(() => {
    if (!selectedCompanyId) return;
    fetchRequests(selectedCompanyId);
  }, [selectedCompanyId, fetchRequests]);

  /** Fokusoi create-modalin otsikko inputtiin kun modaali aukeaa. */
  useEffect(() => {
    if (!isCreateOpen) return;
    setTimeout(() => createTitleRef.current?.focus(), 0);
  }, [isCreateOpen]);

  /** Yhtenäinen “busy wrapper” kaikille sivun async-toiminnoille. */
  const runAction = useCallback(
    async (fn) => {
      if (busy) return;
      setBusy(true);
      try {
        return await fn();
      } finally {
        setBusy(false);
      }
    },
    [busy]
  );

  const openTokenRef = useRef(0);
  /** Avaa palvelupyynnön detaljin heti listan rivin datalla ja täydentää taustalla. */
  const openRequest = useCallback(
    async (rowItem) => {
      if (!rowItem?.id || !selectedCompanyId) return;

      const token = ++openTokenRef.current;

      // 1) Näytä heti listan datalla
      setActive(rowItem);
      resetFromEntity(rowItem, { force: true });
      setMessageText("");
      setView("detail");

      // 2) Täydennä taustalla detalji
      try {
        const detail = await fetchRequestById(selectedCompanyId, rowItem.id);
        if (!detail) return;
        if (openTokenRef.current !== token) return;

        syncFromEntityIfSafe(detail);
      } catch (e) {
        console.error(e);
      }
    },
    [
      selectedCompanyId,
      setActive,
      resetFromEntity,
      fetchRequestById,
      syncFromEntityIfSafe,
    ]
  );

  /** Sulje detalji ja palaa listaan. */
  const closeRequest = useCallback(() => {
    setActive(null);
    setMessageText("");
    setView("list");
    // nollaa editori
    resetFromEntity(null, { force: true });
  }, [setActive, resetFromEntity]);

  /* =============================================================================
     SAVE + STATUS-TOIMINNOT
  ============================================================================= */

  /** Päivitä active-request optimistisesti (UI ei jää jälkeen). */
  const applyOptimisticActiveUpdate = useCallback(
    (patch) => {
      if (!activeRequest?.id) return;
      setActive((prev) =>
        prev && prev.id === activeRequest.id
          ? { ...prev, ...patch, updatedAt: Date.now() }
          : prev
      );
    },
    [setActive, activeRequest?.id]
  );

  /** Luo automaattiset “muutosnote”-tekstit tallennukseen (tyyppi/tila/prioriteetti). */
  const buildAutoNotes = useCallback(
    (prev, next) => {
      const notes = [
        prev?.type !== next?.type &&
          `Palvelupyynnön tyypiksi muutettu ${TYPE_LABEL[next.type]}`,
        prev?.status !== next?.status &&
          `Palvelupyynnön tilaksi muutettu ${STATUS_LABEL[next.status]}`,
        prev?.priority !== next?.priority &&
          `Palvelupyynnön prioriteetiksi asetettu ${PRIORITY_LABEL[next.priority]}`,
      ].filter(Boolean);
      return notes;
    },
    []
  );

  /** Tallenna muokatut kentät (draft -> backend), päivitä UI optimistisesti ja lisää auto-notes. */
  const saveActiveRequest = useCallback(() => {
    if (!activeRequest) return;
    if (!selectedCompanyId) return;
    if (!dirty) return;

    const patch = {
      type: draft.type,
      status: draft.status,
      priority: draft.priority || "normal",
      apartmentId: draft.apartmentId || "",
      description: draft.description || "",
    };

    const actorName = getUserDisplayName(user);
    const prevSnapshot = {
      type: activeRequest.type,
      status: activeRequest.status,
      priority: activeRequest.priority,
    };

    return runAction(async () => {
      try {
        await updateRequest(selectedCompanyId, activeRequest.id, patch);
        // optimistic UI
        applyOptimisticActiveUpdate(patch);
        // draft + base linjaan => dirty false
        commitBase({ ...draft, ...patch });
        setMany(patch);
        // auto notes
        const notes = buildAutoNotes(prevSnapshot, patch);
        for (const body of notes) {
          await addMessage(selectedCompanyId, activeRequest.id, {
            body,
            visibility: "public",
            senderName: actorName,
          });
        }
        showToast(
          `Palvelupyyntö #${activeRequest.number} päivitetty`,
          "success",
          1800
        );
      } catch (e) {
        console.error(e);
        if (String(e?.code || "") === "conflict") {
          openConflict(e, "save");
          return;
        }
        showToast("Tallennus epäonnistui", "error", 2500);
      }
    });
  }, [
    activeRequest,
    selectedCompanyId,
    dirty,
    draft,
    user,
    runAction,
    updateRequest,
    applyOptimisticActiveUpdate,
    commitBase,
    setMany,
    buildAutoNotes,
    addMessage,
    showToast,
    openConflict,
  ]);

  /** Vaihda tila “heti” (yksi napin toiminto): backend + auto-message + optimistic UI + draft/base sync. */
  const changeStatusImmediate = useCallback(
    (newStatus, autoMessageBody) => {
      if (!activeRequest) return;
      if (!selectedCompanyId) return;

      const actorName = getUserDisplayName(user);

      return runAction(async () => {
        try {
          await updateRequest(selectedCompanyId, activeRequest.id, {
            status: newStatus,
          });
          await addMessage(selectedCompanyId, activeRequest.id, {
            body: autoMessageBody,
            visibility: "public",
            senderName: actorName,
          });
          // optimistic UI
          applyOptimisticActiveUpdate({ status: newStatus });
          // draft + base linjaan => ei jää dirtyksi
          setField("status", newStatus);
          commitBase({ ...(draft || {}), status: newStatus });
          showToast(
            `Palvelupyyntö #${activeRequest.number} päivitetty`,
            "success",
            1800
          );
        } catch (e) {
          console.error(e);
          if (String(e?.code || "") === "conflict") {
            openConflict(e, "status");
            return;
          }
          showToast("Toiminto epäonnistui", "error", 2500);
        }
      });
    },
    [
      activeRequest,
      selectedCompanyId,
      user,
      runAction,
      updateRequest,
      addMessage,
      applyOptimisticActiveUpdate,
      setField,
      commitBase,
      draft,
      showToast,
      openConflict,
    ]
  );

  /** Ota pyyntö käsittelyyn (uusi -> vastaanotettu). */
  const handleTakeIn = () =>
    changeStatusImmediate("vastaanotettu", "Palvelupyyntö otettu käsittelyyn");

  /** Sulje pyyntö (-> suljettu). */
  const handleClose = () =>
    changeStatusImmediate("suljettu", "Palvelupyyntö suljettu");

  /** Avaa pyyntö uudelleen (suljettu -> vastaanotettu). */
  const handleReopen = () =>
    changeStatusImmediate("vastaanotettu", "Palvelupyyntö avattu uudelleen");

  /* =============================================================================
     LIITTEET
  ============================================================================= */

  /** Lataa liitteet aktiiviselle pyynnölle. */
  const handleUploadAttachments = useCallback(
    async (files) => {
      if (!activeRequest || !files?.length || !selectedCompanyId) return;
      await runAction(async () => {
        try {
          await uploadAttachments(selectedCompanyId, activeRequest.id, files);
          showToast("Liitteet lisätty", "success", 1600);
        } catch (e) {
          console.error(e);
          showToast("Liitteen lisääminen epäonnistui", "error", 2500);
        }
      });
    },
    [activeRequest, selectedCompanyId, runAction, uploadAttachments, showToast]
  );

  /** Poista liite aktiiviselta pyynnöltä. */
  const handleDeleteAttachment = useCallback(
    async (attId) => {
      if (!activeRequest || !attId || !selectedCompanyId) return;
      await runAction(async () => {
        try {
          await deleteAttachment(selectedCompanyId, activeRequest.id, attId);
          showToast("Liite poistettu", "success", 1600);
        } catch (e) {
          console.error(e);
          showToast("Liitteen poisto epäonnistui", "error", 2500);
        }
      });
    },
    [activeRequest, selectedCompanyId, runAction, deleteAttachment, showToast]
  );

  /* =============================================================================
     VIESTIT / KESKUSTELU
  ============================================================================= */

  /** ✅ Voiko käyttäjä poistaa viestin (oma viesti + ei systeemiviesti). */
  const canDeleteMessage = useCallback(
    (m) => {
      if (!m) return false;
      if (m.isDeleted) return false;

      const uid = user?.uid || null;
      if (!uid) return false;

      if (String(m.senderId || "") !== String(uid)) return false;

      const body = String(m.body || "").trim();
      if (
        body === "Palvelupyyntö suljettu" ||
        body === "Palvelupyyntö otettu käsittelyyn" ||
        body === "Palvelupyyntö avattu uudelleen" ||
        body.startsWith("Palvelupyynnön tyypiksi muutettu") ||
        body.startsWith("Palvelupyynnön tilaksi muutettu") ||
        body.startsWith("Palvelupyynnön prioriteetiksi asetettu")
      ) {
        return false;
      }

      return true;
    },
    [user?.uid]
  );

  /** ✅ Poista oma viesti (soft delete backendissä). */
  const handleDeleteMessage = useCallback(
    async (messageId) => {
      if (!activeRequest || !selectedCompanyId || !messageId) return;
      try {
        await deleteMessage(selectedCompanyId, activeRequest.id, messageId);
        showToast("Viesti poistettu", "success", 1600);
      } catch (e) {
        console.error(e);
        const code = e?.code || e?.message || "";
        if (String(code).includes("cannot_delete_system_message")) {
          showToast("Tätä viestiä ei voi poistaa", "error", 2200);
        } else if (String(code).includes("forbidden")) {
          showToast("Voit poistaa vain omia viestejä", "error", 2200);
        } else {
          showToast("Viestin poisto epäonnistui", "error", 2500);
        }
      }
    },
    [activeRequest, selectedCompanyId, deleteMessage, showToast]
  );

  /** Lähetä viesti aktiivisen pyynnön keskusteluun. */
  const sendMessage = useCallback(async () => {
    if (!activeRequest || isSendingMessage || !selectedCompanyId) return;

    const body = (messageText || "").trim();
    if (!body) return;

    setIsSendingMessage(true);
    try {
      await addMessage(selectedCompanyId, activeRequest.id, {
        body,
        visibility: perms.canWriteInternal ? messageVisibility : "public",
        senderName: getUserDisplayName(user),
      });
      setMessageText("");
    } catch (e) {
      console.error(e);
      showToast("Viestin lähetys epäonnistui", "error", 2500);
    } finally {
      setIsSendingMessage(false);
    }
  }, [
    activeRequest,
    isSendingMessage,
    selectedCompanyId,
    messageText,
    messageVisibility,
    perms.canWriteInternal,
    user,
    addMessage,
    showToast,
  ]);

  /* =============================================================================
     LUO UUSI PYYNTÖ
  ============================================================================= */
  const resetCreateForm = useCallback(() => {
    setCreateRequestType("huolto");
    setCreateDraft({
      title: "",
      apartmentId: "",
      description: "",
      priority: "normal",
    });
  }, []);

  /** Avaa create-modaali ja nollaa tarvittavat kentät. */
  const openCreateModal = useCallback(() => {
    setIsCreateOpen(true);
  }, []);

  /** Sulje create-modaali (ei tee mitään backendissä). */
  const closeCreateModal = useCallback(() => {
    setIsCreateOpen(false);
    resetCreateForm();
  }, [resetCreateForm]);

  /** Lähetä uuden pyynnön luonti backendille ja refetch list. */
  const submitCreate = useCallback(async () => {
    if (!selectedCompanyId) return;
    if (!createDraft.title.trim()) return;

    const creatorName = getUserDisplayName(user);

    await runAction(async () => {
      try {
        await createRequest(selectedCompanyId, {
          type: createRequestType,
          title: createDraft.title.trim(),
          apartmentId: createDraft.apartmentId || "",
          priority: createDraft.priority || "normal",
          description: createDraft.description || "",
          createdBy: {
            uid: user?.uid || null,
            name: creatorName,
            email: user?.sähköposti || "",
          },
        });
        // reset create UI
        setIsCreateOpen(false);
        resetCreateForm();
        showToast("Palvelupyyntö luotu", "success", 1800);
        // refetch list
        fetchRequests(selectedCompanyId);
      } catch (e) {
        console.error(e);
        showToast("Luonti epäonnistui", "error", 2500);
      }
    });
  }, [
    selectedCompanyId,
    createDraft,
    createRequestType,
    user,
    runAction,
    createRequest,
    showToast,
    fetchRequests,
  ]);

  /* =============================================================================
     RENDER HELPERS
  ============================================================================= */
  const renderMobileList = () => (
  <div className={styles.mobileListWrap}>
    <div className={styles.mobileSearchWrap}>
       <span className={styles.mobileSearchIcon} aria-hidden="true">
         <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
            <circle
              cx="11"
              cy="11"
              r="6.5"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M16 16L21 21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </span>
      <input
        className={styles.mobileSearchInput}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Hae palvelupyyntöjä..."
      />
    </div>

    <div className={styles.mobileFilterRow}>
      {[
        { value: "all", label: "Kaikki" },
        { value: "uusi", label: "Uusi" },
        { value: "tyon_alla", label: "Työn alla" },
        { value: "valmis", label: "Valmis" },
        { value: "suljettu", label: "Suljettu" },
      ].map((item) => (
        <button
          key={item.value}
          className={
            statusFilter === item.value
              ? styles.mobileFilterActive
              : styles.mobileFilter
          }
          onClick={() => setStatusFilter(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>

    <div className={styles.mobileCards}>
      {loading && <div className={styles.mobileEmpty}>Ladataan...</div>}

      {!loading && filteredMobileRequests.length === 0 && (
        <div className={styles.mobileEmpty}>Ei palvelupyyntöjä.</div>
      )}

      {!loading &&
        mobileRequests.map((r) => (
         <button
           key={r.id}
           className={styles.mobileRequestCard}
           onClick={() => openRequest(r)}
         >
          <div className={styles.mobileRequestTopRow}>
            <div className={styles.mobileRequestNumber}>#{r.number}</div>
            <span
              className={`${styles.mobileStatusBadge} ${styles[`st_${r.status}`]}`}
            >
              {STATUS_LABEL[r.status] || r.status}
            </span>
          </div>
         <div className={styles.mobileRequestTitle}>{r.title}</div>
         <div className={styles.mobileRequestMeta}>
           <span>{fmtDateTime(r.updatedAt)}</span>
           <span>•</span>
           <span className={styles.mobileRequestType}>
            {TYPE_LABEL[r.type] || r.type}
           </span>
           <span className={styles.mobileChevron}>›</span>
         </div>
        </button>
        ))}
       {!loading && filteredMobileRequests.length > mobileVisibleCount && (
         <div className={styles.mobileLoadMoreWrap}>
           <div className={styles.mobileResultCount}>
             Näytetään{" "}
             {Math.min(mobileVisibleCount, filteredMobileRequests.length)} /{" "}
             {filteredMobileRequests.length}
           </div>
           <button
             type="button"
             className={styles.mobileLoadMoreBtn}
             onClick={() => setMobileVisibleCount((prev) => prev + 7)}
           >
             Näytä lisää
           </button>
         </div>
        )}
    </div>
    <button className={styles.mobileCreateBtn} onClick={openCreateModal}>
      <span>+</span>
      Uusi pyyntö
    </button>
  </div>
);
  /** Renderöi listanäkymän. */
  const renderList = () => (
  <>
    {/* DESKTOP */}
    <div className={styles.desktopList}>
      <SimpleTable
        columns={columns}
        data={visibleRequests}
        loading={loading}
        initialSort={{ key: "updatedAt", dir: "desc" }}
        onRowClick={openRequest}
        actionsHeader=""
        actionsRender={(r) => (
          <div className={styles.rowMenu} onClick={(e) => e.stopPropagation()}>
            <button className={styles.menuBtn}>⋮</button>
            <div className={styles.menuDropdown}>
              <div className={styles.menuItem} onClick={() => openRequest(r)}>
                Avaa
              </div>
              {canDeleteRequest && (
                <div
                  className={styles.menuItemDanger}
                  onClick={() => askDeleteRequest(r)}
                >
                  Poista
                </div>
              )}
            </div>
          </div>
        )}
        filters={{
          pills: [
            {
              field: "status",
              value: statusFilter,
              onChange: setStatusFilter,
              items: [
                { value: "all", label: "Kaikki" },
                { value: "uusi", label: STATUS_LABEL.uusi },
                { value: "vastaanotettu", label: STATUS_LABEL.vastaanotettu },
                { value: "tyon_alla", label: STATUS_LABEL.tyon_alla },
                { value: "valmis", label: STATUS_LABEL.valmis },
                { value: "suljettu", label: STATUS_LABEL.suljettu },
              ],
            },
          ],
          selects: [
            {
              field: "type",
              value: typeFilter,
              onChange: setTypeFilter,
              options: [
                { value: "all", label: "Kaikki tyypit" },
                { value: "huolto", label: TYPE_LABEL.huolto },
                { value: "muutostyo", label: TYPE_LABEL.muutostyo },
                { value: "avain", label: TYPE_LABEL.avain },
                { value: "asiakirja", label: TYPE_LABEL.asiakirja },
                { value: "viesti", label: TYPE_LABEL.viesti },
              ],
            },
          ],
        }}
        search={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: "Haku: #ID, otsikko, asunto…",
          keys: (r) =>
            `${r.number ?? ""} ${r.title ?? ""} ${r.apartmentId ?? ""}`,
        }}
        pageSize={7}
        tableClassName={styles.userTable}
        headClassName={styles.tableHead}
      />
    </div>

    {/* MOBILE */}
    <div className={styles.mobileOnly}>
      {renderMobileList()}
    </div>
  </>
);

  /** Renderöi detaljinäkymän. */
  const renderDetail = () => {
    if (!activeRequest) return null;

    const isSaveEnabled = !!dirty && !isBusy;
    const saveLabel = isBusy ? "Tallennetaan…" : "Tallenna";

    return (
      <div className={styles.userlistCard}>
        <div className={styles.detailHeader}>
          <div className={styles.detailTitle}>
            <h2 className={styles.detailH2}>
              #{activeRequest.number} {activeRequest.title}
            </h2>
            <span
              className={`${styles.statusBtn} ${styles[`st_${draft.status || activeRequest.status}`]}`}
              title="Tila"
            >
              {STATUS_LABEL[draft.status || activeRequest.status]}
            </span>
          </div>

          <div className={styles.headerActions}>
            {perms.canTakeIn && activeRequest.status === "uusi" && (
              <button
                className={styles.stateButton}
                onClick={handleTakeIn}
                disabled={isBusy}
              >
                Ota käsittelyyn
              </button>
            )}

            {perms.canClose &&
              activeRequest.status !== "suljettu" &&
              activeRequest.status !== "uusi" && (
                <button
                  className={styles.stateButton}
                  onClick={handleClose}
                  disabled={isBusy}
                >
                  Sulje pyyntö
                </button>
              )}

            {perms.canReopen && activeRequest.status === "suljettu" && (
              <button
                className={styles.stateButton}
                onClick={handleReopen}
                disabled={isBusy}
              >
                Avaa uudelleen
              </button>
            )}

            <button
              className={
                isSaveEnabled ? styles.primaryBtn : styles.primaryBtnDisabled
              }
              onClick={saveActiveRequest}
              disabled={!isSaveEnabled}
              title={!dirty ? "Ei muutoksia tallennettavaksi" : ""}
            >
              {saveLabel}
            </button>
          </div>
        </div>

        <div className={styles.detailGrid}>
          <div className={styles.leftCol}>
               <h3 className={styles.sectionTitle}>Käsittely</h3>
            <div className={styles.kvRow}>
              <div>
                <div className={styles.kvLabel}>Luotu</div>
                <div className={styles.kvValue}>
                  {fmtDateTime(activeRequest.createdAt)}
                </div>
              </div>
              <div>
                <div className={styles.kvLabel}>Päivitetty</div>
                <div className={styles.kvValue}>
                  {fmtDateTime(activeRequest.updatedAt)}
                </div>
              </div>
              <div>
                <div className={styles.kvLabel}>Tekijä</div>
                <div className={styles.kvValue}>
                  {activeRequest.createdBy?.name ||
                    activeRequest.createdBy?.uid ||
                    "Tuntematon"}
                  {activeRequest.createdBy?.email && (
                    <span className={styles.creatorEmail}>
                      {" "}
                      ({activeRequest.createdBy.email})
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.formRowGrid}>
              <div className={styles.formCol}>
                <label className={styles.label}>Tyyppi</label>
                <select
                  className={styles.input}
                  value={draft.type}
                  onChange={(e) => setField("type", e.target.value)}
                  disabled={!perms.canEditType || isBusy}
                >
                  <option value="huolto">{TYPE_LABEL.huolto}</option>
                  <option value="muutostyo">{TYPE_LABEL.muutostyo}</option>
                  <option value="avain">{TYPE_LABEL.avain}</option>
                  <option value="asiakirja">{TYPE_LABEL.asiakirja}</option>
                  <option value="viesti">{TYPE_LABEL.viesti}</option>
                </select>
              </div>

              <div className={styles.formCol}>
                <label className={styles.label}>Tila</label>
                <select
                  className={styles.input}
                  value={draft.status}
                  onChange={(e) => setField("status", e.target.value)}
                  disabled={!perms.canEditStatus || isBusy}
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.formRowGrid}>
              <div className={styles.formCol}>
                <label className={styles.label}>Prioriteetti</label>
                <select
                  className={styles.input}
                  value={draft.priority}
                  onChange={(e) => setField("priority", e.target.value)}
                  disabled={!perms.canEditPriority || isBusy}
                >
                  {PRIORITY_ORDER.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_LABEL[p]}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formCol}>
                <label className={styles.label}>Asunto</label>
                <input
                  className={styles.input}
                  placeholder="Esim. A12"
                  value={draft.apartmentId}
                  onChange={(e) => setField("apartmentId", e.target.value)}
                  disabled={!perms.canEditApartment || isBusy}
                />
              </div>
            </div>
            <h3 className={styles.sectionTitle}>Kuvaus</h3>
            <div className={styles.formRow}>
              <textarea
                className={`${styles.input} ${styles.inputArea}`}
                placeholder="Kuvaile ongelma tai pyyntö…"
                value={draft.description}
                onChange={(e) => setField("description", e.target.value)}
                disabled={isBusy}
              />
            </div>
            <h3 className={styles.sectionTitle}>Dokumentit</h3>
            <AttachmentsPanel
              attachments={activeRequest?.attachments || []}
              onUpload={handleUploadAttachments}
              onDelete={handleDeleteAttachment}
              disabled={isBusy}
              resetKey={activeRequest.id}
            />
          </div>

          <div className={styles.rightCol}>
            <h3 className={styles.sectionTitle}>Keskustelu</h3>

            <textarea
              className={`${styles.input} ${styles.chatInput}`}
              placeholder="Kirjoita viesti…"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              disabled={isSendingMessage}
            />

            <div className={styles.chatControls}>
              <div className={styles.radioGroup}>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="vis"
                    checked={messageVisibility === "public"}
                    onChange={() => setMessageVisibility("public")}
                    disabled={isSendingMessage}
                  />{" "}
                  Näkyy kaikille
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="vis"
                    checked={messageVisibility === "internal"}
                    onChange={() => setMessageVisibility("internal")}
                    disabled={!perms.canWriteInternal || isSendingMessage}
                  />{" "}
                  Näkyy vain hallitukselle
                </label>
              </div>

              <button
                className={styles.primaryBtn}
                onClick={sendMessage}
                disabled={!messageText.trim() || isSendingMessage}
              >
                {isSendingMessage ? "Lähetetään…" : "Lähetä"}
              </button>
            </div>

            <MessageThread
              messages={visibleMessages}
              pageSize={5}
              resetOn={activeRequest?.id}
              canDeleteMessage={canDeleteMessage} // ✅ LISÄYS
              onDeleteMessage={handleDeleteMessage} // ✅ LISÄYS
            />
          </div>
        </div>
      </div>
    );
  };

  /** Renderöi create-modalin. */
  const renderCreateModal = () => {
    if (!isCreateOpen) return null;

    return (
      <>
        <div className={styles.modalBackdrop} onClick={closeCreateModal} />
        <div
          className={styles.modalRoot}
          role="dialog"
          aria-modal="true"
          onClick={closeCreateModal}
        >
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={closeCreateModal}>
              ×
            </button>

            <h3 className={styles.modalTitleLarge}>Uusi palvelupyyntö</h3>

            <div className={styles.formRowGrid}>
              <div className={styles.formCol}>
                <label className={styles.label}>Tyyppi</label>
                <select
                  className={styles.input}
                  value={createRequestType}
                  onChange={(e) => setCreateRequestType(e.target.value)}
                  disabled={isBusy}
                >
                  <option value="huolto">{TYPE_LABEL.huolto}</option>
                  <option value="muutostyo">{TYPE_LABEL.muutostyo}</option>
                  <option value="avain">{TYPE_LABEL.avain}</option>
                  <option value="asiakirja">{TYPE_LABEL.asiakirja}</option>
                  <option value="viesti">{TYPE_LABEL.viesti}</option>
                </select>
              </div>

              <div className={styles.formCol}>
                <label className={styles.label}>Prioriteetti</label>
                <select
                  className={styles.input}
                  value={createDraft.priority}
                  onChange={(e) =>
                    setCreateDraft((f) => ({ ...f, priority: e.target.value }))
                  }
                  disabled={isBusy}
                >
                  {PRIORITY_ORDER.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_LABEL[p]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <label className={styles.label}>Otsikko</label>
              <input
                ref={createTitleRef}
                className={styles.input}
                placeholder="Esim. Kellarin lamppu rikki"
                value={createDraft.title}
                onChange={(e) =>
                  setCreateDraft((f) => ({ ...f, title: e.target.value }))
                }
                disabled={isBusy}
              />
            </div>

            <div className={styles.formRowGrid}>
              <div className={styles.formCol}>
                <label className={styles.label}>Asunto (valinn.)</label>
                <input
                  className={styles.input}
                  placeholder="Esim. A12"
                  value={createDraft.apartmentId}
                  onChange={(e) =>
                    setCreateDraft((f) => ({
                      ...f,
                      apartmentId: e.target.value,
                    }))
                  }
                  disabled={isBusy}
                />
              </div>
              <div className={styles.formCol}>
                <label className={styles.label}></label>
                <div className={styles.mutedSmall}></div>
              </div>
            </div>

            <div className={styles.formRow}>
              <label className={styles.label}>Kuvaus</label>
              <textarea
                className={styles.inputArea}
                placeholder="Kuvaile ongelma tai pyyntö lyhyesti…"
                value={createDraft.description}
                onChange={(e) =>
                  setCreateDraft((f) => ({
                    ...f,
                    description: e.target.value,
                  }))
                }
                disabled={isBusy}
              />
            </div>

            <div>
              <button
                className={styles.primaryBtn}
                onClick={submitCreate}
                disabled={isBusy || !createDraft.title.trim()}
              >
                Luo pyyntö
              </button>
              <button
                className={styles.cancelBtnTight}
                onClick={closeCreateModal}
                disabled={isBusy}
              >
                Peruuta
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  // ✅ Konflikti-modaali
  const renderConflictModal = () => {
    if (!conflict.open) return null;

    const serverVersion =
      conflict.server?.version ?? conflict.server?.requestVersion ?? null;

    const myVersion = activeRequest?.version ?? null;

    return (
      <>
        <div className={styles.modalBackdrop} onClick={closeConflict} />
        <div
          className={styles.modalRoot}
          role="dialog"
          aria-modal="true"
          onClick={closeConflict}
        >
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={closeConflict}>
              ×
            </button>

            <h3 className={styles.modalTitleLarge}>Tiedot ovat vanhentuneet</h3>

            <div className={styles.mutedSmall2} style={{ marginBottom: 12 }}>
              Toinen käyttäjä on päivittänyt tämän palvelupyynnön sillä välin.
              {serverVersion !== null && (
                <>
                  <br />
                  <span className={styles.mutedSmall2}>
                    (Uusin versio: {serverVersion}
                    {myVersion !== null ? `, sinun versiosi: ${myVersion}` : ""})
                  </span>
                </>
              )}
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                className={styles.primaryBtn}
                onClick={reloadLatestAndDiscardLocal}
              >
                Lataa uusimmat (hylkää omat muutokset)
              </button>

              <button className={styles.cancelBtnTight} onClick={closeConflict}>
                Jatka muokkausta
              </button>
            </div>

            <div className={styles.mutedSmall2} style={{ marginTop: 10 }}>
              Jos jatkat muokkausta, tallennus voi epäonnistua uudelleen ennen
              <br />  kuin lataat uusimmat.  <br />
            </div>
          </div>
        </div>
      </>
    );
  };

  // ✅ Poistovahvistus (kuten Announcements.js)
  const renderDeleteModal = () => {
    if (!showDeleteModal || !selectedRequestForDelete) return null;

    return (
      <Modal
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedRequestForDelete(null);
        }}
        onConfirm={confirmDeleteRequest}
        showCancelButton
        size="small"
      >
        <p className="centerAligned">
          Haluatko varmasti poistaa palvelupyynnön #
          {selectedRequestForDelete.number}?
        </p>
      </Modal>
    );
  };

  /* =============================================================================
     RENDER
  ============================================================================= */
  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={styles.pageTitle}>
          <div className={styles.titleIcon}>
            <IconServicerequests />
          </div>
          <h1>Palvelupyynnöt</h1>

          {view === "list" && (
            <div className={styles.mlAuto}>
              <button className={styles.primaryBtn} onClick={openCreateModal}>
                + Uusi pyyntö
              </button>
            </div>
          )}

          {view === "detail" && (
            <div className={styles.mlAuto}>
              <button className={styles.backLink} onClick={closeRequest}>
                ← Takaisin palvelupyyntöihin
              </button>
            </div>
          )}
        </div>

        {view === "list" && renderList()}
        {view === "detail" && activeRequest && renderDetail()}

        {renderCreateModal()}
        {renderConflictModal()}
        {renderDeleteModal()}
      </div>
    </div>
  );
}